/**
 * Dashboard client — page d'accueil de l'espace client.
 *
 * Affiche la « Vue d'ensemble » uniquement : la navigation entre sections
 * (Mes documents, Mes offres, Mes demandes, Mes factures, Messagerie,
 * Assistance) se fait via la sidebar gauche (DashboardLayout), pas via
 * une rangée d'onglets redondante.
 */
import type { Project } from '@bcl/shared';
import { useQuery } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { computePayloadProgressPercent } from '@/features/projects/booking-progress';
import { listProjects } from '@/features/projects/projects-api';
import { statusLabels } from '@/features/projects/labels';
import {
  DashboardOverview,
  type DashboardDocument,
  type DashboardInvoice,
  type DashboardRequest,
} from '@/features/dashboard/overview';
import { useAuth } from '@/lib/auth-context';

function projectToRequest(p: Project): DashboardRequest {
  return {
    id: p.reference,
    address: p.info?.address.line1 ?? 'Adresse à compléter',
    status: statusLabels[p.status],
    date: new Date(p.createdAt).toLocaleDateString('fr-FR'),
    progress: computePayloadProgressPercent(p),
  };
}

export function DashboardPage() {
  const { user } = useAuth();
  const projects = useQuery({
    queryKey: ['projects', 'mine'],
    queryFn: listProjects,
  });

  const requests: DashboardRequest[] = (projects.data ?? []).map(
    projectToRequest,
  );
  const documents: DashboardDocument[] = [];
  const invoices: DashboardInvoice[] = [];

  const userData = {
    name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
    plan:
      requests[0] && projects.data?.[0]?.packCode
        ? `Pack ${projects.data[0].packCode.replace('_', ' ')}`
        : 'Aucun pack sélectionné',
    address: requests[0]?.address ?? 'Aucune adresse renseignée',
  };

  return (
    <DashboardLayout title="Tableau de bord">
      <div className="space-y-4 mt-6 px-2 sm:px-4">
        {projects.isLoading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : (
          <DashboardOverview
            userData={userData}
            requests={requests}
            documents={documents}
            invoices={invoices}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
